import { React, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, InputAdornment } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import CustomInput from "../../CustomInput/CustomInput";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  setOpenAgentLoginFlow,
  setOpenAgentSignupFlow,
  setAgentSignupStepCount
} from "../../../redux/login";
import { LoginAgent } from "../../../services/LoginUser";
import { ReactComponent as EyeIcon } from '../../../assets/svg/visible.svg';
import { ReactComponent as OffEyeIcon } from '../../../assets/svg/invisible.svg';
import ForgetPassword from "../../Dialog/ForgetPasswordModal";
import { toast } from "react-toastify";
import Loader from "../../loader/Loader";

const AgentLogin = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openForgetPwdDialog, setOpenForgetPwdDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('password');

  const handleToggle = () => {
    setType(type === 'password' ? 'text' : 'password');
  }

  const handleLogin = async (values, { resetForm }) => {
    try {
      setLoading(true);
      let formData = new FormData();
      formData.append("email", values?.email);
      formData.append("password", values?.password);
      formData.append("remember_token", 1);

      const extraConfig = {
        headers: { "content-type": "multipart/form-data" },
      };

      const { success, data } = await dispatch(LoginAgent(formData, extraConfig));
      setLoading(false);

      if (success) {
        if (data?.email_verified) {
          dispatch(setOpenAgentLoginFlow(false));
          navigate("/agent-dashboard");
          toast.success(t("AgentLogin.Congratulations"), {
            autoClose: 2500,
            className: "toast-message",
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          dispatch(setAgentSignupStepCount(4));
          dispatch(setOpenAgentLoginFlow(false));
          dispatch(setOpenAgentSignupFlow(true));
        }
      }

      resetForm({ email: "", password: "" });
    } catch (e) {
      console.log(e, "err");
      setLoading(false);
      resetForm({ email: "", password: "" });
    }
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("AgentLogin.PleaseEnterEmail"))
        .required(t("AgentLogin.PleaseEnterEmail")),
      password: Yup.string().required(t("AgentLogin.PleaseEnterYourPassword")),
    }),
    onSubmit: handleLogin,
  });

  return (
    <>
      {loading && <Loader />}
      <ForgetPassword openForgetPwdDialog={openForgetPwdDialog} setOpenForgetPwdDialog={setOpenForgetPwdDialog} />

      <form onSubmit={formik.handleSubmit} noValidate>
        <Box sx={{ p: { xs: 1, md: 2 }, background: '#FFFFFF' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.3rem",
              color: "#1B2559",
              fontFamily: "Poppins, sans-serif",
              textAlign: 'center',
              width: '100%',
              mb: 3
            }}
          >
            {t("AgentLogin.Agent_Login")}
          </Typography>
          {/* <Typography
            sx={{
              fontSize: "0.875rem",
              color: "#64748B",
              textAlign: "center",
              mb: 4
            }}
          >
            Welcome back! Please enter your details.
          </Typography> */}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomInput
              variant="outlined"
              fullWidth
              label={t("LogIn.EmailAddress") + "*"}
              placeholder={t("LogIn.EmailAddress")}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#F4F7FE'
                },
                '& .MuiInputLabel-root': {
                  color: '#1B2559',
                  fontWeight: 600,
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif'
                }
              }}
              value={formik.values.email}
              name="email"
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />

            <Box>
              <CustomInput
                type={type}
                variant="outlined"
                fullWidth
                label={t("LogIn.Password") + "*"}
                placeholder={t("LogIn.Password")}
                onChange={(e) => { formik.handleChange(e); setPassword(e.target.value) }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#F4F7FE'
                  },
                  '& .MuiInputLabel-root': {
                    color: '#1B2559',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'Poppins, sans-serif'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
                      {type === 'password' ?
                        <OffEyeIcon style={{ width: 22, height: 22, fill: '#0085FD' }} /> :
                        <EyeIcon style={{ width: 22, height: 22, fill: '#0085FD' }} />
                      }
                    </InputAdornment>
                  ),
                }}
                value={formik.values.password}
                name="password"
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
              <Box sx={{ mt: 1, textAlign: 'right' }}>
                <Typography
                  onClick={() => {
                    localStorage.setItem('role', 'agent');
                    setOpenForgetPwdDialog(true);
                  }}
                  sx={{
                    fontSize: '0.8125rem',
                    color: '#0085FD',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {t("LogIn.forgotYourPassword")}
                </Typography>
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disableElevation
              sx={{
                bgcolor: "#0085FD",
                color: "white",
                py: 1.5,
                borderRadius: "12px",
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                boxShadow: '0px 10px 20px rgba(0, 133, 253, 0.2)',
                "&:hover": {
                  bgcolor: "#0072DB",
                  boxShadow: '0px 12px 24px rgba(0, 133, 253, 0.3)',
                },
              }}
            >
              {t("LogIn.signIn")}
            </Button>

            <Typography
              sx={{
                textAlign: 'center',
                fontSize: '0.875rem',
                color: '#64748B',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {t("LogIn.DontHaveAnAccount")}{" "}
              <Box
                component="span"
                onClick={() => {
                  dispatch(setAgentSignupStepCount(1));
                  dispatch(setOpenAgentSignupFlow(true));
                  dispatch(setOpenAgentLoginFlow(false));
                }}
                sx={{
                  color: "#0085FD",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: 'Poppins, sans-serif',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {t("LogIn.SignUp")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default AgentLogin;
